// File: src/components/common/DetailViewActionBar.js
// Copyright (c) 2025 DefoAI UG (haftungsbeschraenkt)
// All rights reserved.
//
// Action bar component for detail views with edit/save/cancel functionality.
// Provides consistent UI for view/edit mode switching and status feedback.

import React from 'react';
import { Box, Button, CircularProgress, Alert, Collapse, Chip } from '@mui/material';
import { Edit, Save, Cancel } from '@mui/icons-material';

/**
 * DetailViewActionBar - Action bar for detail view edit functionality
 *
 * @param {Object} props
 * @param {boolean} props.isEditing - Whether currently in edit mode
 * @param {boolean} props.isSaving - Whether save operation is in progress
 * @param {boolean} props.hasUnsavedChanges - Whether there are unsaved changes
 * @param {function} props.onEdit - Callback to enter edit mode
 * @param {function} props.onSave - Callback to save changes
 * @param {function} props.onCancel - Callback to cancel editing
 * @param {string|null} props.error - Error message to display
 * @param {string|null} props.success - Success message to display
 * @param {function} props.onClearMessages - Callback to clear error/success messages
 * @param {React.ReactNode} [props.children] - Additional content to render in the action bar
 * @param {boolean} [props.disabled] - Whether the edit button should be disabled
 * @param {string} [props.entityName] - Name of the entity being edited (for button labels)
 */
const DetailViewActionBar = ({
    isEditing,
    isSaving,
    hasUnsavedChanges,
    onEdit,
    onSave,
    onCancel,
    error,
    success,
    onClearMessages,
    children,
    disabled = false,
    entityName = 'Changes',
}) => {
    // Gradient styling for buttons
    const gradientButtonStyle = {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        '&:hover': {
            background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
        },
        '&.Mui-disabled': {
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            opacity: 0.6,
            color: 'rgba(255, 255, 255, 0.7)',
        },
    };

    return (
        <Box>
            {/* Alert Messages */}
            <Collapse in={Boolean(error)}>
                <Alert
                    severity="error"
                    onClose={onClearMessages}
                    sx={{ mb: 2 }}
                    data-testid="view-alert-error"
                >
                    {error}
                </Alert>
            </Collapse>
            <Collapse in={Boolean(success)}>
                <Alert
                    severity="success"
                    onClose={onClearMessages}
                    sx={{ mb: 2 }}
                    data-testid="view-alert-success"
                >
                    {success}
                </Alert>
            </Collapse>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {!isEditing ? (
                    // Read-only mode: Show Edit button
                    <Button
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={onEdit}
                        disabled={disabled}
                        sx={gradientButtonStyle}
                        data-testid="view-action-edit"
                    >
                        Edit {entityName}
                    </Button>
                ) : (
                    // Edit mode: Show Save, Cancel buttons and unsaved indicator
                    <>
                        <Button
                            variant="contained"
                            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                            onClick={onSave}
                            disabled={isSaving || !hasUnsavedChanges}
                            sx={gradientButtonStyle}
                            data-testid="view-action-save"
                        >
                            {isSaving ? 'Saving...' : `Save ${entityName}`}
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Cancel />}
                            onClick={onCancel}
                            disabled={isSaving}
                            color="inherit"
                            data-testid="view-action-cancel"
                        >
                            Cancel
                        </Button>
                        {hasUnsavedChanges && (
                            <Chip
                                label="Unsaved changes"
                                size="small"
                                color="warning"
                                data-testid="view-unsaved-indicator"
                            />
                        )}
                    </>
                )}

                {/* Additional content passed as children */}
                {children}
            </Box>
        </Box>
    );
};

export default DetailViewActionBar;
